import { Controller } from "@hotwired/stimulus"

const AMBER_ROW_CLASSES = [
  "bg-amber-50/50",
  "dark:bg-amber-900/10",
  "hover:bg-amber-50",
  "dark:hover:bg-amber-900/20"
]

const GREEN_ROW_CLASSES = [
  "bg-emerald-50/60",
  "dark:bg-emerald-900/10",
  "hover:bg-emerald-50",
  "dark:hover:bg-emerald-900/20"
]

const AMBER_BADGE_CLASSES = [
  "bg-amber-100",
  "dark:bg-amber-900/40",
  "text-amber-700",
  "dark:text-amber-300"
]

const GREEN_BADGE_CLASSES = [
  "bg-emerald-100",
  "dark:bg-emerald-900/40",
  "text-emerald-700",
  "dark:text-emerald-300"
]

export default class extends Controller {
  static targets = ["checkbox", "badge", "timestamp", "message"]
  static values = { url: String }

  connect() {
    this.currentSent = this.checkboxTarget.checked
    this.currentTimestamp = this.timestampTarget.textContent.trim()
  }

  async toggle() {
    if (this.updating) return

    const previousSent = this.currentSent
    const previousTimestamp = this.currentTimestamp
    const requestedSent = this.checkboxTarget.checked

    this.setUpdating(true)
    this.hideMessage()

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: new URLSearchParams({ receipt_sent: requestedSent ? "1" : "0" })
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || "The receipt status could not be updated.")

      this.currentSent = Boolean(data.receipt_sent)
      this.currentTimestamp = data.receipt_sent_at_label || ""
      this.checkboxTarget.checked = this.currentSent
      this.updateVisuals(this.currentSent, this.currentTimestamp)
      this.adjustSummaryCounts(previousSent, this.currentSent)
    } catch (error) {
      this.checkboxTarget.checked = previousSent
      this.updateVisuals(previousSent, previousTimestamp)
      this.showMessage(error.message)
    } finally {
      this.setUpdating(false)
    }
  }

  markAsReceipted(event) {
    this.currentSent = true
    this.currentTimestamp = event.detail?.timestamp || ""
    this.checkboxTarget.checked = true
    this.updateVisuals(true, this.currentTimestamp)
  }

  setUpdating(updating) {
    this.updating = updating
    this.checkboxTarget.disabled = updating
    this.element.toggleAttribute("aria-busy", updating)
    this.element.classList.toggle("opacity-60", updating)
  }

  updateVisuals(sent, timestamp) {
    const row = this.element.closest("tr")
    if (row) {
      row.classList.remove(...AMBER_ROW_CLASSES, ...GREEN_ROW_CLASSES)
      row.classList.add(...(sent ? GREEN_ROW_CLASSES : AMBER_ROW_CLASSES))
    }

    this.badgeTarget.classList.remove(...AMBER_BADGE_CLASSES, ...GREEN_BADGE_CLASSES)
    this.badgeTarget.classList.add(...(sent ? GREEN_BADGE_CLASSES : AMBER_BADGE_CLASSES))
    this.badgeTarget.textContent = sent ? "Marked as receipted" : "Mark as receipted"

    this.timestampTarget.textContent = timestamp
    this.timestampTarget.classList.toggle("hidden", !timestamp)
  }

  adjustSummaryCounts(previousSent, currentSent) {
    if (previousSent === currentSent) return

    const needsDelta = currentSent ? -1 : 1
    const sentDelta = currentSent ? 1 : -1

    this.adjustCount(document.getElementById("receipt-needs-count"), needsDelta)
    this.adjustCount(document.getElementById("receipt-sent-count"), sentDelta)
  }

  adjustCount(element, delta) {
    if (!element) return

    const currentValue = Number.parseInt(element.textContent.replace(/[^0-9-]/g, ""), 10)
    if (Number.isNaN(currentValue)) return

    element.textContent = new Intl.NumberFormat().format(Math.max(currentValue + delta, 0))
  }

  hideMessage() {
    this.messageTarget.textContent = ""
    this.messageTarget.classList.add("hidden")
  }

  showMessage(message) {
    this.messageTarget.textContent = message
    this.messageTarget.classList.remove("hidden")
  }
}
